import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Input, Type } from '@angular/core';

import { Observable } from 'rxjs';

import { animate, style, transition, trigger } from '@angular/animations';
import { LanguageModule } from '@language';
import { ApiQuery } from '../../../core/api/entities/api-query.entity';
import { ExtendedFacetProfile } from '../../../core/profiles/entities/extended-profile.entity';
import { ExtendedFacetProfileField } from '../../../core/profiles/entities/extended-profile-field.entity';
import { FacetProfileField } from '../../../core/profiles/entities/profile-field.entity';
import { ShFacetListComponent } from '../../../elements/components/facet-list/facet-list.component';
import { ShFacetPlaceHolderComponent } from '../../../elements/components/facet-placeholder/facet-placeholder.component';
import { ShFacetSummaryComponent } from '../../../elements/components/facet-summary/facet-summary.component';
import { ShFacet, ShSelectedFacetValue } from '../../../elements/components/facet/entities/facet.entity';
import { COMPLETABLE_TASK_FACET } from '../../constants/virtual-facets.constants';
import { ShTaskFacetDataService } from '../../data-services/task-facet.data-service';
import { TaskFacetSearchDialogConfig } from '../../entities/facet-search-dialog.entity';
import { CompletableTaskFacet, ExtractOnlyCompletableValue } from '../../helpers/task-facet.helper';
import { ShProfileFacetListContainerComponent } from '../profile-facet-list/profile-facet-list-container.component';
import { ShTaskFacetSearchDialogComponent } from '../task-facet-search-dialog/task-facet-search-dialog.component';

/**
 * The facet panel of the three Taken pages. It is the record facet panel, with four differences: the
 * facets come from the tasks endpoint, it synthesises the "Mijn taken" filter, removing that filter has
 * to rebuild the query itself, and "toon meer" opens the task dialog.
 */
@Component({
    standalone: true,
    selector: 'sh-task-facet-list',
    templateUrl: './task-facet-list-container.component.html',
    styleUrls: ['../profile-facet-list/profile-facet-list-container.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, ShFacetSummaryComponent, ShFacetListComponent, ShFacetPlaceHolderComponent, LanguageModule],
    providers: [],
    animations: [
        // Prevent from appearing for 10ms
        trigger('fadeIn', [transition(':enter', [style({ display: 'none' }), animate('0ms 10ms', style({}))])]),
        // Prevent from disappearing for 10ms
        trigger('fadeOut', [transition(':leave', [style({}), animate('0ms 10ms', style({}))])])
    ]
})
export class ShTaskFacetListContainerComponent extends ShProfileFacetListContainerComponent {
    /**
     * The panel's own data service. It is a field of its own rather than an override of the base's: a
     * subclass field is assigned after the base constructor, so the base can keep a plain `inject`.
     */
    private readonly taskFacetsService: ShTaskFacetDataService = inject(ShTaskFacetDataService);

    protected override searchDialogComponent: Type<unknown> = ShTaskFacetSearchDialogComponent;

    @Input() includeOnlyCompletableFacet = false;

    /**
     * On by default: all three Taken pages offer the same 13-15 facetgroepen and want them draggable, so
     * the call sites do not each repeat it.
     */
    @Input() override reorderable = true;

    protected override pinnedFacetKeys: ReadonlySet<string> = new Set<string>([COMPLETABLE_TASK_FACET]);

    protected override fetchFacets(query: ApiQuery, facetProfile: ExtendedFacetProfile): Observable<ShFacet[]> {
        return this.taskFacetsService.getProfileFacets(
            query,
            facetProfile.Id,
            facetProfile.Fields,
            this.getOnlyCompletable(query)
        );
    }

    protected override virtualFacets(selectedFacets: ShSelectedFacetValue[]): ShFacet[] {
        const offered: boolean =
            this.includeOnlyCompletableFacet ||
            selectedFacets.some(selected => selected.key === COMPLETABLE_TASK_FACET);

        return offered ? [CompletableTaskFacet(this.translateService)] : [];
    }

    /** "Mijn taken" is never part of the structured query, so removing it moves nothing the base watches. */
    protected override extraQueryTriggers(): Observable<unknown>[] {
        return [this.urlQueryService.getSelectedFacets()];
    }

    protected override searchDialogData(
        facetProfileField: ExtendedFacetProfileField,
        allFacetProfileFields: FacetProfileField[],
        selectedFacets: ShSelectedFacetValue[],
        query: ApiQuery,
        facetProfileId: string
    ): TaskFacetSearchDialogConfig {
        return {
            ...super.searchDialogData(facetProfileField, allFacetProfileFields, selectedFacets, query, facetProfileId),
            onlyCompletable: this.getOnlyCompletable(query)
        };
    }

    // #region OnlyCompletable

    private getOnlyCompletable(query: ApiQuery): boolean {
        // Default value of onlyCompletable is opposite of showOnlyCompletable:
        // If we don't show the completable filter (mediahaven case, value = false): user can only see completable tasks (true)
        // If we do show the completable filter (digihaven case, value = true): we show all tasks if the user has deselected the filter (false)
        return ExtractOnlyCompletableValue(query, !this.includeOnlyCompletableFacet);
    }
    // #endregion
}
